import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { getMasterFileDuplicates } from "../lib/api";

const BLUE   = "#0176CE";
const ORANGE = "#F37428";

function truncate(s, n) {
  if (!s) return "—";
  return s.length > n ? s.slice(0, n) + "…" : s;
}

function TypeBadge({ type }) {
  if (type === "cross") return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 20,
      background: "rgba(239,68,68,0.08)", color: "#ef4444", letterSpacing: "0.04em" }}>
      CROSS-SYSTEM
    </span>
  );
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 20,
      background: "rgba(1,118,206,0.1)", color: BLUE, letterSpacing: "0.04em" }}>
      SAME SYSTEM
    </span>
  );
}

function GroupCard({ group, type, navigate }) {
  const [open, setOpen] = useState(true);

  const subtitle = type === "domain"
    ? group.domain
    : (() => {
        const [letter, digits] = (group.fingerprint || ":").split(":");
        return `Name starts with "${letter}" · Digit key: ${digits}`;
      })();

  return (
    <div style={styles.groupCard}>
      <div style={styles.groupHeader} onClick={() => setOpen(o => !o)}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
          <span style={styles.groupCode}>{group.code}</span>
          {type === "domain" && <TypeBadge type={group.type} />}
          <span style={styles.groupSubtitle}>{subtitle}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
          <span style={styles.groupCount}>{group.count} records</span>
          <span style={{ color: "#9ca3af", fontSize: 14 }}>{open ? "▲" : "▼"}</span>
        </div>
      </div>

      {open && (
        <table style={styles.recTable}>
          <thead>
            <tr>
              <th style={styles.recTh}>System</th>
              <th style={styles.recTh}>Vendor #</th>
              <th style={styles.recTh}>Name</th>
              {type === "domain"
                ? <th style={styles.recTh}>Domain</th>
                : <>
                    <th style={styles.recTh}>Uploaded Address</th>
                    <th style={styles.recTh}>Standardized Address</th>
                  </>}
              <th style={styles.recTh}>Location</th>
              <th style={{ ...styles.recTh, width: 32 }}></th>
            </tr>
          </thead>
          <tbody>
            {group.records.map((r, i) => {
              const name    = r.company_name_recommended || r.name1_std || r.original_name || "—";
              const loc     = [r.city_std, r.state_std, r.country_std].filter(Boolean).join(", ");
              const origAddr = [r.address1_orig, r.city_orig, r.state_orig, r.zip_orig].filter(Boolean).join(", ");
              return (
                <tr
                  key={r.master_record_id}
                  style={{ ...styles.recRow, background: i % 2 === 0 ? "#fff" : "#fafafa", cursor: "pointer" }}
                  onClick={() => navigate(`/results/${r.last_job_id}/records/${r.row_number}?filter=all`)}
                >
                  <td style={styles.recTd}><span style={{ fontSize: 11, color: "#94a3b8" }}>{r.system_name}</span></td>
                  <td style={styles.recTd}><span style={{ fontSize: 12, color: "#6b7280" }}>{r.original_num}</span></td>
                  <td style={styles.recTd}><span style={{ fontWeight: 500, fontSize: 13 }}>{name}</span></td>
                  {type === "domain"
                    ? <td style={styles.recTd}><span style={{ fontSize: 12, color: BLUE }}>{r.domain_value || "—"}</span></td>
                    : <>
                        <td style={styles.recTd}><span style={{ fontSize: 12, color: "#6b7280" }}>{truncate(origAddr || r.address1_orig, 40) || "—"}</span></td>
                        <td style={styles.recTd}><span style={{ fontSize: 12, color: "#374151" }}>{truncate(r.address1_std, 40) || "—"}</span></td>
                      </>}
                  <td style={styles.recTd}><span style={{ fontSize: 12, color: "#6b7280" }}>{loc || "—"}</span></td>
                  <td style={{ ...styles.recTd, textAlign: "right", color: "#9ca3af" }}>›</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function MasterFileDuplicates() {
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();

  const initTab  = searchParams.get("tab")  || "domain";
  const initType = searchParams.get("type") || "all";

  const [tab,     setTab]     = useState(initTab);
  const [typeFilter, setTypeFilter] = useState(initType); // "all"|"same"|"cross"
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  useEffect(() => {
    getMasterFileDuplicates()
      .then(setData)
      .catch(e => setError(e.message || "Failed to load duplicates."))
      .finally(() => setLoading(false));
  }, []);

  const domGroups = (data?.domain_groups || []).filter(g =>
    typeFilter === "all" ? true : g.type === typeFilter
  );
  const adrGroups = data?.address_groups || [];
  const s = data?.summary || {};

  return (
    <div style={styles.page}>
      <div style={styles.topStripe} />
      <div style={styles.body}>
      <Sidebar active="master-file" />
      <main style={styles.main}>

        <div style={styles.headerRow}>
          <div>
            <Link to="/master-file" style={styles.backLink}>← Back to Master File</Link>
            <h1 style={styles.pageTitle}>Potential Duplicates</h1>
            <p style={styles.pageSub}>
              {s.domain_records || 0} records in domain groups · {s.address_records || 0} records in address groups
            </p>
          </div>
        </div>

        {/* Summary cards */}
        {data && (
          <div style={styles.summaryRow}>
            <div style={styles.summaryCard}>
              <div style={styles.summaryVal}>{s.domain_same_groups || 0}</div>
              <div style={styles.summaryLabel}>Same-system domain groups</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={{ ...styles.summaryVal, color: "#ef4444" }}>{s.domain_cross_groups || 0}</div>
              <div style={styles.summaryLabel}>Cross-system domain groups</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={{ ...styles.summaryVal, color: "#f59e0b" }}>{s.address_groups || 0}</div>
              <div style={styles.summaryLabel}>Address fingerprint groups</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div style={styles.tabs}>
          <button style={{ ...styles.tab, ...(tab === "domain"  ? styles.tabActive : {}) }} onClick={() => setTab("domain")}>
            Domain Duplicates {data ? `(${s.domain_groups || 0})` : ""}
          </button>
          <button style={{ ...styles.tab, ...(tab === "address" ? styles.tabActive : {}) }} onClick={() => setTab("address")}>
            Address Matches {data ? `(${s.address_groups || 0})` : ""}
          </button>
        </div>

        {/* Domain type filter */}
        {tab === "domain" && data && (
          <div style={styles.filterRow}>
            {[["all","All"], ["cross","Cross-system"], ["same","Same system"]].map(([v, l]) => (
              <button
                key={v}
                style={{ ...styles.filterBtn, ...(typeFilter === v ? styles.filterBtnActive : {}) }}
                onClick={() => setTypeFilter(v)}
              >
                {l}
              </button>
            ))}
          </div>
        )}

        {loading && <div style={styles.empty}>Analyzing…</div>}
        {error   && <div style={{ color: "#ef4444", padding: "12px 0" }}>{error}</div>}

        {!loading && !error && tab === "domain" && (
          domGroups.length === 0
            ? <div style={styles.emptyBox}>No domain duplicates found for this filter.</div>
            : domGroups.map(g => <GroupCard key={g.code} group={g} type="domain" navigate={navigate} />)
        )}

        {!loading && !error && tab === "address" && (
          adrGroups.length === 0
            ? <div style={styles.emptyBox}>No address fingerprint matches found.</div>
            : adrGroups.map(g => <GroupCard key={g.code} group={g} type="address" navigate={navigate} />)
        )}
      </main>
      </div>
    </div>
  );
}

const styles = {
  page:        { display: "flex", flexDirection: "column", minHeight: "100vh", background: "#f8fafc" },
  topStripe:   { height: 4, background: `linear-gradient(90deg, ${BLUE}, ${ORANGE})`, flexShrink: 0 },
  body:        { display: "flex", flex: 1 },
  main:        { flex: 1, padding: "40px 48px", overflowY: "auto" },
  headerRow:   { marginBottom: 24 },
  backLink:    { color: "#6b7280", fontSize: 13, textDecoration: "none", display: "block", marginBottom: 8 },
  pageTitle:   { fontSize: 28, fontWeight: 700, color: "#111827", margin: 0 },
  pageSub:     { color: "#6b7280", fontSize: 14, margin: "4px 0 0" },
  empty:       { color: "#9ca3af", padding: "40px 0", textAlign: "center" },
  emptyBox:    { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "40px", textAlign: "center", color: "#6b7280", fontSize: 14 },

  summaryRow:  { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 24 },
  summaryCard: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "20px 24px" },
  summaryVal:  { fontSize: 28, fontWeight: 700, color: "#0f172a", lineHeight: 1, marginBottom: 4 },
  summaryLabel:{ fontSize: 12, color: "#6b7280", fontWeight: 500 },

  tabs:        { display: "flex", gap: 4, marginBottom: 16, borderBottom: "2px solid #e2e8f0", paddingBottom: 0 },
  tab:         { background: "transparent", border: "none", padding: "10px 20px", fontSize: 14, fontWeight: 500, color: "#6b7280", cursor: "pointer", borderBottom: "2px solid transparent", marginBottom: -2 },
  tabActive:   { color: BLUE, borderBottom: `2px solid ${BLUE}`, fontWeight: 700 },

  filterRow:   { display: "flex", gap: 8, marginBottom: 16 },
  filterBtn:   { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 20, padding: "5px 14px", fontSize: 12, fontWeight: 500, color: "#374151", cursor: "pointer" },
  filterBtnActive: { background: BLUE, color: "#fff", borderColor: BLUE },

  groupCard:   { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, marginBottom: 12, overflow: "hidden" },
  groupHeader: { display: "flex", alignItems: "center", padding: "14px 20px", cursor: "pointer", gap: 12, background: "#fafafa", borderBottom: "1px solid #f1f5f9" },
  groupCode:   { fontSize: 11, fontWeight: 700, fontFamily: "monospace", color: "#374151", background: "#e2e8f0", padding: "2px 8px", borderRadius: 4, flexShrink: 0 },
  groupSubtitle:{ fontSize: 13, color: "#374151", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  groupCount:  { fontSize: 12, color: "#6b7280", fontWeight: 600 },

  recTable:    { width: "100%", borderCollapse: "collapse" },
  recTh:       { padding: "8px 16px", fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", textAlign: "left" },
  recRow:      { borderBottom: "1px solid #f1f5f9", transition: "background 0.1s" },
  recTd:       { padding: "11px 16px", fontSize: 13, color: "#374151", verticalAlign: "middle" },
};
